import { jsPDF } from "jspdf";

export const RESUME_HEADLINE =
  "Senior Software Engineer | Python | Java | TypeScript | Rust | C++ | Cloud | Algorithms | MCP | Debugging & Performance";

export const RESUME_TEXT = `OVIDIU MURA

${RESUME_HEADLINE}

Portland, Oregon, United States | Remote | Contractor
(971) 990-9319 | ovidiu.mura@yahoo.com
LinkedIn: linkedin.com/in/ovidiu-m-200573107 | GitHub: github.com/ovimura

================================================================================
PROFESSIONAL SUMMARY
================================================================================

Senior Software Engineer with 15+ years of production experience across bug fixing, feature implementation, codebase refactoring, and performance optimization. Strong in Python, Java, TypeScript/JavaScript, Rust, and C++, with additional Go, plus a CS foundation (B.S. and M.S.) in algorithms, data structures, and systems. Implemented a Rust program at PacteraEdge to process CSV data files as part of operational data workflows.

Recent work includes building Model Context Protocol (MCP) servers and tools so AI agents can call real systems through structured contracts; debugging agent and tool failures; and producing maintainable, testable software that can serve as a reference solution. Previously evaluated AI-generated code for correctness, quality, maintainability, and performance (DataAnnotation.tech) across Python, JavaScript, C++, C#, and SQL.

Comfortable designing reproducible engineering work: clear problem statements, deterministic checks (tests, expected outputs, root cause analysis), and golden implementations. Experienced on large-scale distributed codebases (Bank of America, Adidas, Nike, UPS) and on remote, cross-functional teams with written design, code review, and production support.

================================================================================
CORE SKILLS
================================================================================

Languages: Python 3, Java, TypeScript, JavaScript, Rust, C++, Go, C#, SQL

Software Engineering: Bug fixing / root cause analysis, feature implementation, codebase refactoring, performance optimization, unit and automated tests, golden / reference implementations

Algorithms & Systems: Data structures, algorithms, complexity-minded performance tuning, distributed systems, scalability, API / service design

MCP & AI Agents: MCP tool servers, agentic workflows, LangChain / LangGraph, LLM tool use, AI coding agents, evaluating generated code vs tests and specs

Cloud & Delivery: AWS, Azure, GCP, Docker, Kubernetes, Terraform, CI/CD, Linux, event-driven services, microservices

================================================================================
KEY PROJECTS
================================================================================

MCP Tool Servers & Agent-Solvable Engineering Work
TEKsystems / Symbotic
- Built MCP servers/tools and Python/TypeScript services so AI agents discover and invoke real APIs under structured contracts (auth, retries, error handling).
- Debugged complex agent/runtime failures: bad tool arguments, context loss, authorization, and integration defects; converted them into durable code and contract fixes.
- Implemented features and refactored prototypes into reviewed, documented, testable services suitable as reference implementations.
Tech: Python, TypeScript, JavaScript, MCP, REST APIs, Docker, Kubernetes, GKE, GCP

Agent Workflows, Tests & Human-Verified Solutions
US Tech Solutions
- Built LangChain / LangGraph / MCP workflows where agents call external systems; added logging, tests, security controls, and human-approval gates so outcomes are checkable.
- Used AI coding agents for implementation and debugging, then validated generated changes with tests and technical review.
Tech: Python, TypeScript, LangChain, LangGraph, MCP, LLMs, AWS, REST APIs, Docker, Kubernetes

AI Coding Evaluation: Correctness, Maintainability, Performance
DataAnnotation.tech
- Wrote and reviewed programming solutions for an AI-powered coding platform; judged code on correctness, quality, maintainability, and performance.
- Worked across Python, JavaScript, HTML, C++, C#, and SQL — identifying bugs, weak abstractions, and faster alternatives.
Tech: Python, JavaScript, C++, C#, SQL, Code Review

Rust CSV Data Processing
PacteraEdge — UPS / Markaaz
- Implemented a Rust program to process CSV data files for operational data workflows — parsing records, transforming fields, and producing deterministic outputs for downstream pipelines.
- Used Rust for performance-minded file processing alongside Python/Spark pipelines and Java/Python automation on the same project.
Tech: Rust, CSV, Python, Java, Spark / PySpark, Data Processing

================================================================================
PROFESSIONAL EXPERIENCE
================================================================================

TEKsystems / Symbotic
Senior Software Engineer | Mar 2026 – Aug 2026
- Implement and maintain production Python/TypeScript services; own bug fixes, features, and refactors through tests and review.
- Design MCP tools that expose real systems to AI agents; debug tool-use and runtime failures; improve contracts and performance.
- Collaborate remotely with distributed engineering teams on design tradeoffs, code review, and operational reliability.

US Tech Solutions
Software Engineer | Apr 2025 – Feb 2026
- Delivered Python backend features and MCP/LangGraph agent workflows; investigated defects and shipped maintainable fixes.
- Added tests, logging, and human-in-the-loop checks so agent behavior can be verified deterministically.
- Deployed containerized services on AWS; refactored evaluation prototypes into reusable components.

Highlight Technologies
Software Engineer / Full-Stack Developer | Mar 2024 – Mar 2025
- Implemented TypeScript/Python product features; refactored and optimized event-driven AWS services (DynamoDB, SNS, SQS).
- Debugged application, data, and deployment issues; Terraform/CI/CD; production support.

DataAnnotation.tech
Software Engineer | Jan 2024 – Feb 2024
- Created and evaluated coding solutions for AI training/evaluation: correctness, style, maintainability, and performance.
- Reviewed multi-language implementations (Python, JavaScript, C++, C#, SQL) against expected behavior.

Apex Systems / Bank of America
Application Architect | Dec 2021 – Dec 2023
- Led design and refactoring of large-scale distributed financial platforms (Java, Python, C#, SQL, AWS/Azure).
- Code review, root cause analysis, performance/scalability, and production support; promoted only changes that met maintainability and security bars.

PacteraEdge — UPS / Markaaz — Portland, OR
Full-Stack Developer | Jan 2020 – Nov 2021
- Implemented a Rust program to process CSV data files for operational workflows; also delivered Java/Python automation, Node.js APIs, and Spark/PySpark pipelines.
- Debugged API, data, and deployment issues in cloud B2C systems.

Infosys — Adidas — Portland, OR
Technology Lead | Apr 2018 – Dec 2019
- Production defect resolution and platform enhancements with globally distributed (remote) teams.
- Specs, test plans, go-live docs; Docker/Kubernetes; mentored engineers.

Earlier Experience
- Intel — Portland, OR | Python Developer / Validation / Firmware | 2016 – 2018. C++ and Python tooling, REST APIs, automated tests, performance-oriented validation, Linux.
- Nike — Portland, OR | Software Engineer | 2015 – 2016. Java test automation; JMeter API performance testing during AWS migration; Jenkins CI.
- McKesson Health Solutions — Portland, OR | Software Engineer | 2012 – 2015. Java/Spring production applications; APIs; SQL; full SDLC in regulated healthcare.
- Fiserv — Corvallis, OR | Software Engineer | 2010 – 2012. Java financial-platform software; Oracle/SQL Server; production delivery.

================================================================================
EDUCATION
================================================================================

Master of Science (M.S.) in Computer Science — Portland State University | 2017 – 2020
Bachelor of Science (B.S.) in Computer Science — Portland State University | 2006 – 2009
Graduate Certificate in Computer Science — Portland State University | 2020

================================================================================
CERTIFICATIONS
================================================================================

- Production AI Agents with LangChain + LangGraph — 2026 (Udemy)
- Oracle Certified Professional — Java SE6 Programmer
- Certified Deep Learning Specialization
- HashiCorp Vault Certification
`;

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadResumeText() {
  const blob = new Blob([RESUME_TEXT], { type: "text/plain;charset=utf-8" });
  triggerDownload(blob, "Ovidiu_Mura_Resume.txt");
}

export function downloadResumePdf() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 54;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeWrapped = (text, options = {}) => {
    const {
      size = 10,
      style = "normal",
      color = [31, 41, 55],
      gap = 4,
      indent = 0,
    } = options;
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxWidth - indent);
    lines.forEach((line) => {
      ensureSpace(size + 6);
      doc.text(line, margin + indent, y);
      y += size + 3;
    });
    y += gap;
  };

  writeWrapped("OVIDIU MURA", {
    size: 18,
    style: "bold",
    color: [17, 24, 39],
    gap: 6,
  });
  writeWrapped(RESUME_HEADLINE, {
    size: 10,
    style: "bold",
    color: [15, 118, 110],
    gap: 4,
  });
  writeWrapped(
    "Portland, Oregon, United States | Remote | Contractor",
    { size: 9, color: [75, 85, 99], gap: 2 }
  );
  writeWrapped(
    "(971) 990-9319  |  ovidiu.mura@yahoo.com  |  linkedin.com/in/ovidiu-m-200573107  |  github.com/ovimura",
    { size: 9, color: [15, 118, 110], gap: 10 }
  );

  const sections = RESUME_TEXT.split(
    /={80,}\r?\n([A-Z &]+)\r?\n={80,}\r?\n/
  ).slice(1);

  for (let i = 0; i < sections.length; i += 2) {
    const heading = sections[i].trim();
    const body = (sections[i + 1] || "").trim();

    ensureSpace(28);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(heading, margin, y);
    y += 6;
    doc.setDrawColor(0, 152, 121);
    doc.setLineWidth(1.2);
    doc.line(margin, y, pageWidth - margin, y);
    y += 14;

    const bodyLines = body.split(/\n/);
    for (const rawLine of bodyLines) {
      const line = rawLine.trimEnd();
      if (!line.trim()) {
        y += 6;
        continue;
      }
      if (line.startsWith("- ")) {
        writeWrapped(`• ${line.slice(2)}`, { size: 10, indent: 12, gap: 3 });
        continue;
      }
      if (line.startsWith("Tech:")) {
        writeWrapped(line, { size: 9, style: "italic", color: [75, 85, 99], gap: 8 });
        continue;
      }
      const isLabel = /^(Languages|Software Engineering|Algorithms|MCP|Cloud)/.test(
        line
      );
      const looksLikeHeading =
        !line.startsWith(" ") && line.length < 70 && !line.includes(".");
      writeWrapped(line, {
        size: 10,
        style: isLabel || looksLikeHeading ? "bold" : "normal",
        gap: 4,
      });
    }
    y += 8;
  }

  doc.save("Ovidiu_Mura_Resume.pdf");
}
